// J4 agree and both confirm · J5 say how it went, and reply on the record · standing and Me.

import type { ActorId } from '@model/index.ts';
import { html } from '../html.ts';
import { t } from '../i18n.ts';
import type { Key } from '../i18n.ts';
import { flash, go, local, on, onForm, refuse } from '../core.ts';
import type { Screen } from '../core.ts';
import { world as w } from '../world.ts';
import {
  activePause,
  canSee,
  claim,
  decisionOn,
  finalDecision,
  isAppealDecision,
  repliesTo,
  stewards,
  joinInfo,
  myLive,
  num,
  ofPred,
  param,
  ratingBy,
  releaseOf,
  caseReasonKey,
  reportsBy,
  saidAbout,
  str,
  trade,
  tradesOf,
} from '../queries.ts';
import { back, itemRow, linkButton, personHref, tradesLine } from '../ui.ts';
import { itemArt } from '../art.ts';
import { fmtAgo, fmtDate, fmtPrice } from '../format.ts';
import { offerGate } from '../explain.ts';
import { confirmDialog } from '../dialog.ts';

on('agree', async (el) => {
  const offer = claim(w, el.dataset.id!)!;
  const price = fmtPrice(num(offer, 'priceInr'));
  const ok = await confirmDialog({
    title: t('agree.title', { title: str(offer, 'title'), price }),
    body: t('agree.body', { name: w.name(offer.author), g: w.gender(offer.author) }),
    yes: t('agree.yes'),
  });
  if (!ok) return;
  const { claim: c, d } = await w.act(w.seat, { predicate: 'accept', refs: [offer.id] });
  if (!d.admitted) {
    refuse('accept', t('why.accept'), d);
    return go(`#/item/${offer.id}`);
  }
  go(`#/trade/${c.id}`);
});

// J12: either party may release a deal that fell through, until both confirm (kernel R8).
on('release', async (el) => {
  const id = el.dataset.id!;
  const i = trade(w, id);
  if (!i) return;
  const offer = claim(w, i.offer)!;
  const other = i.buyer === w.me.id ? i.seller : i.buyer;
  const ok = await confirmDialog({
    title: t('release.title'),
    body: t('release.body', { title: str(offer, 'title'), name: w.name(other) }),
    yes: t('release.yes'),
  });
  if (!ok) return;
  const { d } = await w.act(w.seat, { predicate: 'release', refs: [id] });
  if (d.admitted) flash(t('release.done', { title: str(offer, 'title') }));
  else refuse('release', t('why.release'), d);
  go(`#/trade/${id}`);
});

on('complete', async (el) => {
  const id = el.dataset.id!;
  const { d } = await w.act(w.seat, { predicate: 'complete', refs: [id] });
  if (!d.admitted) refuse('complete', t('why.complete'), d);
  go(`#/trade/${id}`);
});

export function tradeScreen(id: string): Screen {
  const i = trade(w, id);
  const me = w.me.id;
  if (!i) return { title: '', tab: 'me', body: html`${back('#/me')}<p>Not found.</p>` };
  const offer = claim(w, i.offer)!;
  const buyer = i.buyer === me;
  const other = buyer ? i.seller : i.buyer;
  const price = fmtPrice(num(offer, 'priceInr'));
  const iConfirmed = i.completedBy.includes(me);
  const theyConfirmed = i.completedBy.includes(other);
  const party = i.buyer === me || i.seller === me;
  const confirms = ofPred(w, 'complete').filter((c) => c.refs[0] === i.id);
  const doneAt = i.done ? Math.max(...confirms.map((c) => c.issuedAt)) : 0;
  const mine = ratingBy(w, i.id, me);
  // The ramp, as the kernel's gate sees it now (R16): a rating received lifts it at once; a settled trade with
  // someone you did not vouch for lifts it rampAfter days after your confirmation.
  const gate = offerGate(w, w.would(w.seat, { predicate: 'offer', body: {} }));
  const received = saidAbout(w, me).length;
  const theyRated = ratingBy(w, i.id, other);
  const limit = param(w, 'offerLimit');
  const myConfirm = confirms.find((c) => c.author === me);
  const settlesOn = myConfirm ? myConfirm.issuedAt + param(w, 'rampAfter') : 0;
  const release = releaseOf(w, i.id);
  const status = (who: ActorId, done: boolean) =>
    html`<li class="${done ? 'done' : ''}"><span>${who === me ? t('common.you') : w.name(who)}</span><span>${done ? t('trades.done') : t('trades.waiting')}</span></li>`;
  return {
    title: str(offer, 'title'),
    tab: 'me',
    body: html`
      ${back('#/me')}
      <h1 tabindex="-1">${str(offer, 'title')}</h1>
      <p class="lead">${buyer ? t('trade.buyer', { name: w.name(other), price }) : t('trade.seller', { name: w.name(other), price })}</p>
      ${release
        ? html`<p class="note" data-testid="released">${release.author === me
            ? t('trade.releasedByMe', { date: fmtDate(release.issuedAt) })
            : t('trade.releasedBy', { name: w.name(release.author), date: fmtDate(release.issuedAt) })}</p>`
        : i.done
        ? html`<p class="done-line" data-testid="trade-done">${t('trade.done', { date: fmtDate(doneAt) })}</p>
            ${gate.gate === 'regular' && theyRated && received === 1
              ? html`<p class="note" data-testid="limit-up">${t('trade.limitUp', { name: w.name(other), limit })}</p>`
              : gate.gate === 'new' && settlesOn
                ? html`<p class="muted" data-testid="limit-when">${t('trade.limitWhen', { name: w.name(other), limit, date: fmtDate(settlesOn), g: w.gender(other) })}</p>`
                : ''}
            ${mine
              ? html`<p>${t('trade.rated', { word: t(`rate.${num(mine, 'score')}` as Key) })}</p>`
              : party
                ? linkButton(t('trade.rate'), `#/rate/${i.id}`)
                : ''}`
        : html`
            ${confirms.length === 0 ? html`<p class="muted">${t('trade.agreedAgo', { ago: fmtAgo(claim(w, i.id)!.issuedAt, w.now()) })}</p>` : ''}
            <p>${t('trade.next')}</p>
            <ul class="confirms">${status(i.buyer, i.completedBy.includes(i.buyer))}${status(i.seller, i.completedBy.includes(i.seller))}</ul>
            ${iConfirmed
              ? html`<p class="note">${t('trade.waiting', { name: w.name(other) })}</p>`
              : html`${theyConfirmed ? html`<p class="note">${t('trade.theyConfirmed', { name: w.name(other) })}</p>` : ''}
                  <button class="btn" type="button" data-act="complete" data-id="${i.id}">${buyer ? t('trade.received') : t('trade.handed')}</button>`}
            ${party
              ? html`<div class="release">
                  <button class="quiet" type="button" data-act="release" data-id="${i.id}">${t('trade.releaseCta')}</button>
                  <p class="muted">${t('trade.releaseHelp')}</p>
                </div>`
              : ''}
          `}
    `,
  };
}

export function rateScreen(id: string): Screen {
  const i = trade(w, id);
  const me = w.me.id;
  if (!i) return { title: '', tab: 'me', body: html`${back('#/me')}<p>Not found.</p>` };
  const other = i.buyer === me ? i.seller : i.buyer;
  const name = w.name(other);
  return {
    title: t('rate.title', { name }),
    tab: 'me',
    body: html`
      ${back(`#/trade/${id}`)}
      <form class="stack" data-form="rate" data-id="${id}" data-subject="${other}">
        <fieldset class="choices">
          <legend><h1 tabindex="-1">${t('rate.title', { name })}</h1></legend>
          ${[5, 4, 3, 2, 1].map(
            (n) => html`<label class="choice"><input type="radio" name="score" value="${n}" required /><span>${t(`rate.${n}` as Key)}</span></label>`,
          )}
        </fieldset>
        <label class="field">
          <span>${t('rate.note')} <small>(${t('common.optional')})</small></span>
          <textarea name="text" rows="2" maxlength="200"></textarea>
        </label>
        <p class="note">${t('rate.record', { name, g: w.gender(other) })}</p>
        <p class="muted">${t('rate.helps')}</p>
        <button class="btn" type="submit">${t('rate.save')}</button>
      </form>
    `,
  };
}

onForm('rate', async (fd, form) => {
  if (!form.reportValidity()) return;
  const id = form.dataset.id!;
  const text = String(fd.get('text') ?? '').trim();
  const { d } = await w.act(w.seat, {
    predicate: 'rate',
    subject: form.dataset.subject!,
    refs: [id],
    body: text ? { score: Number(fd.get('score')), text } : { score: Number(fd.get('score')) },
  });
  if (!d.admitted) refuse('rate', t('why.rate'), d);
  go(`#/trade/${id}`);
});

// A reply on the record: to a rating (J5) or to a steward's decision (J11). The kernel admits it once (respond.once).
export function replyScreen(id: string): Screen {
  const r = claim(w, id);
  if (!r) return { title: '', tab: 'me', body: html`${back('#/me')}<p>Not found.</p>` };
  const decision = r.predicate === 'decide';
  const quoted = decision
    ? html`<blockquote><span class="muted">${w.name(r.author)} · ${fmtDate(r.issuedAt)}</span><br />${str(r, 'note')}</blockquote>`
    : html`<blockquote><span class="muted">${t('said.by', { name: w.name(r.author) })}: ${t(`rate.${num(r, 'score')}` as Key)}</span>
        ${str(r, 'text') ? html`<br />“${str(r, 'text')}”` : ''}</blockquote>`;
  return {
    title: t('reply.cta'),
    tab: decision ? 'sell' : 'me',
    body: html`
      ${back(decision ? '#/sell' : personHref(w.me.id))}
      <h1 tabindex="-1">${t('reply.cta')}</h1>
      ${quoted}
      <form class="stack" data-form="respond" data-id="${id}">
        <label class="field"><span>${t(decision ? 'replyDecision.help' : 'reply.label')}</span><textarea name="text" rows="3" required maxlength="280"></textarea></label>
        <button class="btn" type="submit">${t('reply.save')}</button>
      </form>
    `,
  };
}

// J11: one appeal, to a steward other than the one who decided (decide.other-steward).
export function appealFormScreen(decisionId: string): Screen {
  const d = claim(w, decisionId);
  if (!d) return { title: '', tab: 'sell', body: html`${back('#/sell')}<p>Not found.</p>` };
  return {
    title: t('appeal.title'),
    tab: 'sell',
    body: html`
      ${back('#/sell')}
      <h1 tabindex="-1">${t('appeal.title')}</h1>
      <p class="lead">${t('appeal.body', { steward: w.name(d.author) })}</p>
      <form class="stack" data-form="appeal" data-id="${decisionId}">
        <label class="field"><span>${t('appeal.label')}</span><textarea name="text" rows="4" required maxlength="600"></textarea></label>
        <p class="muted">${t('appeal.who')}</p>
        <button class="btn" type="submit">${t('appeal.send')}</button>
      </form>
    `,
  };
}

onForm('appeal', async (fd, form) => {
  if (!form.reportValidity()) return;
  const me = w.me;
  const { d } = await w.act(me.key, {
    predicate: 'appeal',
    refs: [form.dataset.id!],
    audience: [...new Set([me.id, ...stewards(w)])],
    body: { text: String(fd.get('text')).trim() },
  });
  if (d.admitted) flash(t('appeal.sent'));
  else refuse('appeal', t('why.appeal'), d);
  go('#/sell');
});

onForm('respond', async (fd, form) => {
  if (!form.reportValidity()) return;
  const target = claim(w, form.dataset.id!)!;
  // A reply is seen by whoever can see what it answers.
  const { d } = await w.act(w.seat, { predicate: 'respond', refs: [target.id], audience: target.audience, body: { text: String(fd.get('text')).trim() } });
  if (!d.admitted) refuse('respond', t('why.respond'), d);
  go(target.predicate === 'decide' ? '#/sell' : personHref(w.me.id));
});

// Standing: evidence, never a number. What a neighbour sees.
export function personScreen(key: string): Screen {
  const p = w.byKey.get(key);
  const me = w.me.id;
  if (!p) return { title: '', tab: 'nearby', body: html`${back('#/')}<p>Not found.</p>` };
  const j = joinInfo(w, p.id);
  const said = saidAbout(w, p.id);
  const paused = activePause(w, p.id, me);
  const live = paused ? [] : myLive(w, p.id);
  const self = p.id === me;
  return {
    title: t('standing.title', { name: p.name[w.language] }),
    tab: self ? 'me' : 'nearby',
    body: html`
      ${back(self ? '#/me' : '#/')}
      <h1 tabindex="-1">${p.name[w.language]}</h1>
      ${j
        ? html`<p class="lead">${j.voucher
            ? t('standing.joinedVouch', { date: fmtDate(j.at), voucher: w.name(j.voucher), g: p.g })
            : t('standing.joinedPhone', { date: fmtDate(j.at), g: p.g })}</p>`
        : ''}
      <p class="stat">${tradesLine(p.id)}</p>
      ${paused
        ? html`<p class="state" data-testid="paused-public">${t(isAppealDecision(w, paused) ? 'standing.pausedAfterAppeal' : 'standing.paused', { date: fmtDate(num(paused, 'until')) })}</p>
          ${repliesTo(w, paused.id, me)
            .concat(repliesTo(w, paused.refs[1] ?? '', me))
            .filter((r) => r.author === p.id)
            .slice(0, 1)
            .map((r) => html`<p class="reply"><span class="muted">${t('said.reply', { name: p.name[w.language] })}</span> “${str(r, 'text')}”</p>`)}`
        : ''}
      <h2>${t('standing.said')}</h2>
      <p class="muted">${t('standing.noScore')}</p>
      ${said.length
        ? html`<ul class="said">
            ${said.map(({ rate, reply }) => {
              const offer = claim(w, claim(w, rate.refs[0])?.refs[0]);
              return html`<li>
                <p><b>${t('said.by', { name: w.name(rate.author) })}: ${t(`rate.${num(rate, 'score')}` as Key)}</b>
                  <span class="muted">${offer ? t('said.about', { title: str(offer, 'title') }) : ''} · ${fmtDate(rate.issuedAt)}</span></p>
                ${str(rate, 'text') ? html`<p>“${str(rate, 'text')}”</p>` : ''}
                ${reply
                  ? html`<p class="reply"><span class="muted">${t('said.reply', { name: w.name(reply.author) })}</span> “${str(reply, 'text')}”</p>`
                  : self
                    ? linkButton(t('reply.cta'), `#/reply/${rate.id}`, 'quiet')
                    : ''}
              </li>`;
            })}
          </ul>`
        : html`<p>${t('standing.nothingSaid')}</p>`}
      ${live.length ? html`<h2>${t('standing.forSale')}</h2><ul class="cards">${live.map((o) => itemRow(o, { showSeller: false }))}</ul>` : ''}
      ${!self && w.me.role !== 'newcomer' ? html`<p class="report-link"><a class="quiet" href="#/report/${p.key}">${t('person.report', { name: p.name[w.language] })}</a></p>` : ''}
    `,
  };
}

export function meScreen(): Screen {
  const me = w.me;
  const trades = tradesOf(w, me.id);
  const reports = reportsBy(w, me.id);
  return {
    title: me.name[w.language],
    tab: 'me',
    body: html`
      <h1 tabindex="-1">${me.name[w.language]}</h1>
      <p><a href="${personHref(me.id)}">${t('me.record')}</a></p>
      <h2>${t('trades.title')}</h2>
      ${trades.length
        ? html`<ul class="items">
            ${trades.map((i) => {
              const o = claim(w, i.offer)!;
              const other = i.buyer === me.id ? i.seller : i.buyer;
              return html`<li class="item"><a class="item-link has-art" href="#/trade/${i.id}">
                ${itemArt(str(o, 'title'), 'thumb', str(o, 'kind'))}
                <span class="item-title">${str(o, 'title')}</span>
                <span class="item-price">${fmtPrice(num(o, 'priceInr'))}</span>
                <span class="item-meta">${w.name(other)} · ${i.released ? t('trades.released') : i.done ? t('trades.done') : t('trades.waiting')}</span>
              </a></li>`;
            })}
          </ul>`
        : html`<p class="muted">${t('trades.none')}</p>`}
      ${reports.length
        ? html`<h2>${t('reports.title')}</h2>
            <ul class="items">
              ${reports.map((r) => {
                const first = decisionOn(w, r.id);
                // Follow an appeal to the decision that now stands, if this reporter may see it.
                const last = first ? finalDecision(w, first) : undefined;
                const dec = last && canSee(w, me.id, last) ? last : first;
                const name = w.name(r.subject);
                const base = dec
                  ? str(dec, 'outcome') === 'suspend-offers'
                    ? t('outcome.pause', { date: fmtDate(num(dec, 'until')) })
                    : t('outcome.none')
                  : '';
                const outcome = dec && dec !== first ? t('outcome.afterAppeal', { outcome: base }) : base;
                return html`<li class="item"><div class="item-link">
                  <span class="item-title">${t(caseReasonKey(str(r, 'reason')) as Key, { g: w.gender(r.subject) })}</span>
                  <span class="item-meta">${dec ? t('reports.closed', { name, steward: w.name(dec.author), outcome }) : t('reports.open', { name })}</span>
                </div></li>`;
              })}
            </ul>`
        : ''}
      <ul class="links">
        <li><a href="#/vouch">${t('vouch.title')}</a></li>
        <li><a href="#/rules">${t('me.rules')}</a></li>
      </ul>
      ${languagePicker()}
    `,
  };
}

export function languagePicker() {
  return html`<fieldset class="segmented">
    <legend>${t('me.language')}</legend>
    <label><input type="radio" name="lang" value="en" data-act="lang" ${w.language === 'en' ? 'checked' : ''} /><span>English</span></label>
    <label><input type="radio" name="lang" value="hi" data-act="lang" ${w.language === 'hi' ? 'checked' : ''} /><span lang="hi">हिन्दी</span></label>
  </fieldset>`;
}

on('lang', (el) => {
  w.setLanguage((el as HTMLInputElement).value === 'hi' ? 'hi' : 'en');
});

